// Trackside scenery for Sepang: the main grandstand between the two long
// straights (a fascia with boards, a raked seating deck with the crowd, and
// separate leaf-style canopies, like Sepang's), the pit building (the pit lane
// itself is render/pitLane.ts), vegetation (see vegetation.ts, kept off the pit
// roads) and a forest treeline on the horizon.
// Architecture is built as textured strips with UVs in metres, so textures
// tile at real-world scale instead of stretching across 400 m faces.

import { Color3, DynamicTexture, Mesh, MeshBuilder, Scene, StandardMaterial, Vector3, VertexData } from './babylon';
import type { Track } from '../track/track';
import { barrierOffsetAt } from '../track/geometry';
import { buildVegetation } from './vegetation';
import { barrierTexture } from './textures';
import { param } from '../config/params';
import { sepangPitLane, type PitLane } from '../track/pitLane';

export const sceneryParams = {
  trees: param({ id: 'render.trees', label: 'Tree density', group: 'Render', min: 0, max: 1, step: 0.05, value: 0.6 }),
};

const WRAP = 1;

interface Footprint {
  x: number;
  z: number;
  /** Half extents along the box's own axes and its heading. */
  halfLength: number;
  halfDepth: number;
  heading: number;
}

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function material(scene: Scene, name: string, color: Color3, texture?: DynamicTexture, emissive = 0): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = color;
  m.specularColor = new Color3(0.05, 0.05, 0.05);
  if (texture) m.diffuseTexture = texture;
  m.backFaceCulling = false;
  m.twoSidedLighting = true;
  if (emissive) m.emissiveColor = new Color3(emissive, emissive, emissive);
  return m;
}

/** Canvas texture tiled every `tileU` × `tileV` metres (strip UVs are in metres). */
function tiled(scene: Scene, name: string, w: number, h: number, tileU: number, tileV: number, draw: (ctx: CanvasRenderingContext2D) => void): DynamicTexture {
  const tex = new DynamicTexture(name, { width: w, height: h }, scene, true);
  draw(tex.getContext() as CanvasRenderingContext2D);
  tex.update(true);
  tex.wrapU = WRAP;
  tex.wrapV = WRAP;
  tex.uScale = 1 / tileU;
  tex.vScale = 1 / tileV;
  tex.anisotropicFilteringLevel = 8;
  return tex;
}

/** Seat rows (0.8 m apart) in Sepang's blue and yellow, mostly filled with spectators. */
function crowdTexture(scene: Scene): DynamicTexture {
  // 16 rows over 12.8 m up the rake; 8 m across.
  return tiled(scene, 'tex-crowd', 256, 256, 8, 12.8, (ctx) => {
    const rand = rng(11);
    const shirts = ['#e10600', '#ffffff', '#ffcc33', '#1e5bd8', '#ff7a00', '#1b1b1b', '#2fbf71', '#d7d7d7', '#f5d0b0', '#ff4f9a'];
    for (let row = 0; row < 16; row++) {
      const y = row * 16;
      ctx.fillStyle = row < 4 ? '#e2b52c' : '#2a63c4';
      ctx.fillRect(0, y, 256, 16);
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, y + 13, 256, 3);
      for (let i = 0; i < 32; i++) {
        if (rand() < 0.18) continue;
        const x = i * 8 + rand() * 2;
        ctx.fillStyle = shirts[Math.floor(rand() * shirts.length)]!;
        ctx.fillRect(x, y + 4 + rand() * 2, 5, 7);
        ctx.fillStyle = rand() < 0.7 ? '#2a1d14' : '#d9b48a';
        ctx.fillRect(x + 1, y + 1 + rand() * 2, 3, 3);
      }
    }
  });
}

/** Back of a grandstand: concrete frame with dark openings and stair cores. 10 m × 16 m tile. */
function standBackTexture(scene: Scene): DynamicTexture {
  return tiled(scene, 'tex-stand-back', 256, 256, 10, 16, (ctx) => {
    ctx.fillStyle = '#c9c8c3';
    ctx.fillRect(0, 0, 256, 256);
    // Columns every 5 m and floor slabs every 4 m, with shadowed openings between.
    for (let y = 0; y < 256; y += 64) {
      ctx.fillStyle = '#3a3d42';
      ctx.fillRect(8, y + 12, 112, 44);
      ctx.fillRect(136, y + 12, 112, 44);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fillRect(8, y + 12, 112, 4);
      ctx.fillRect(136, y + 12, 112, 4);
    }
  });
}

/**
 * Underside of a grandstand roof: membrane panels between purlins (every
 * 2 m across the roof) with fine seams. 6 m × 4 m tile.
 */
function roofUndersideTexture(scene: Scene): DynamicTexture {
  return tiled(scene, 'tex-roof-under', 256, 256, 6, 4, (ctx) => {
    const rand = rng(17);
    ctx.fillStyle = '#d8d6d0';
    ctx.fillRect(0, 0, 256, 256);
    // Slight panel-to-panel variation, panels 1.5 m wide.
    for (let x = 0; x < 256; x += 64) {
      ctx.fillStyle = `rgba(${rand() < 0.5 ? '255,255,255' : '90,90,90'},${0.04 + rand() * 0.05})`;
      ctx.fillRect(x, 0, 64, 256);
      ctx.fillStyle = 'rgba(70,70,75,0.18)';
      ctx.fillRect(x, 0, 2, 256);
    }
    // Purlins: dark steel sections with a lit lower edge.
    for (const y of [0, 128]) {
      ctx.fillStyle = '#5a5f66';
      ctx.fillRect(0, y, 256, 12);
      ctx.fillStyle = 'rgba(30,32,36,0.45)';
      ctx.fillRect(0, y + 12, 256, 10);
      ctx.fillStyle = '#9aa0a8';
      ctx.fillRect(0, y + 10, 256, 2);
    }
  });
}

/** Pit building facade: garage doors, a white sign band and a glass upper floor. 16 m × 12 m tile. */
function facadeTexture(scene: Scene): DynamicTexture {
  return tiled(scene, 'tex-facade', 512, 384, 16, 12, (ctx) => {
    const px = 32; // pixels per metre
    const yOf = (m: number): number => 384 - m * px;
    ctx.fillStyle = '#e9e9e6';
    ctx.fillRect(0, 0, 512, 384);
    // Glass band, 6.2–10.6 m, with mullions.
    ctx.fillStyle = '#1d3550';
    ctx.fillRect(0, yOf(10.6), 512, (10.6 - 6.2) * px);
    ctx.fillStyle = 'rgba(160,200,230,0.25)';
    ctx.fillRect(0, yOf(10.6), 512, 24);
    ctx.fillStyle = '#c9ccd0';
    for (let x = 0; x < 512; x += 64) ctx.fillRect(x, yOf(10.6), 4, (10.6 - 6.2) * px);
    // Garage openings, two per tile, with roller doors part-way up.
    for (const gx of [24, 280]) {
      ctx.fillStyle = '#1a1c20';
      ctx.fillRect(gx, yOf(4.6), 208, 4.6 * px);
      ctx.fillStyle = '#b9bdc3';
      ctx.fillRect(gx, yOf(4.6), 208, 1.6 * px);
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      for (let y = yOf(4.6); y < yOf(3.0); y += 6) ctx.fillRect(gx, y, 208, 1);
      // Team board above each garage.
      ctx.fillStyle = gx < 200 ? '#e10600' : '#0b2a5b';
      ctx.fillRect(gx + 40, yOf(5.8), 128, 0.9 * px);
    }
  });
}

/** Measures positions along a straight stretch of track. */
interface StraightFrame {
  length: number;
  heading: number;
  /** World point `along` metres from the start, `out` metres from the centreline on the chosen side, `up` above the ground. */
  point(along: number, out: number, up: number): Vector3;
}

function straightFrame(track: Track, s0: number, s1: number, side: 1 | -1): StraightFrame {
  const a = track.frameAt(s0);
  const b = track.frameAt(s1);
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  const tx = (b.x - a.x) / length;
  const tz = (b.z - a.z) / length;
  // Right-hand normal of the driving direction is (tz, -tx).
  const nx = tz * side;
  const nz = -tx * side;
  const baseY = Math.min(a.y, b.y);
  return {
    length,
    heading: Math.atan2(tx, tz),
    point: (along, out, up) => new Vector3(a.x + tx * along + nx * out, baseY + up, a.z + tz * along + nz * out),
  };
}

/**
 * A strip swept along a straight: `profile` is a list of (out, up) points
 * across it. UVs: u = metres along, v = metres along the profile. Optional
 * `shades` (one per profile point) become vertex colours, e.g. to darken
 * the recessed back of a roof; a material must then only be used by shaded
 * strips (meshes are merged per material).
 */
function strip(scene: Scene, name: string, f: StraightFrame, along0: number, along1: number, profile: Array<[number, number]>, mat: StandardMaterial, shades?: number[]): Mesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const vAt: number[] = [0];
  for (let i = 1; i < profile.length; i++) {
    const [o0, u0] = profile[i - 1]!;
    const [o1, u1] = profile[i]!;
    vAt.push(vAt[i - 1]! + Math.hypot(o1 - o0, u1 - u0));
  }
  for (const along of [along0, along1]) {
    profile.forEach(([out, up], i) => {
      const p = f.point(along, out, up);
      positions.push(p.x, p.y, p.z);
      uvs.push(along, vAt[i]!);
      if (shades) colors.push(shades[i]!, shades[i]!, shades[i]!, 1);
    });
  }
  const n = profile.length;
  for (let i = 0; i < n - 1; i++) indices.push(i, i + 1, n + i, n + i, i + 1, n + i + 1);
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  if (shades) vd.colors = colors;
  const mesh = new Mesh(name, scene);
  vd.applyToMesh(mesh);
  mesh.material = mat;
  return mesh;
}

/**
 * A roof rafter at a fixed distance along a straight, following `profile`
 * just under the roof: a web tapering from `depthBack` (first profile point)
 * to `depthFront` (last), with a bottom flange `width` wide.
 */
function rafter(scene: Scene, f: StraightFrame, along: number, profile: Array<[number, number]>, depthBack: number, depthFront: number, width: number, mat: StandardMaterial): Mesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const n = profile.length;
  let v = 0;
  profile.forEach(([out, up], i) => {
    if (i > 0) v += Math.hypot(out - profile[i - 1]![0], up - profile[i - 1]![1]);
    const d = depthBack + ((depthFront - depthBack) * i) / (n - 1);
    // Per profile point: web top, web bottom, flange left, flange right.
    for (const [da, du] of [
      [0, -0.02],
      [0, -d],
      [-width / 2, -d],
      [width / 2, -d],
    ] as const) {
      const p = f.point(along + da, out, up + du);
      positions.push(p.x, p.y, p.z);
      uvs.push(da, v + du);
    }
  });
  for (let i = 0; i < n - 1; i++) {
    const a = i * 4;
    const b = a + 4;
    indices.push(a, a + 1, b, b, a + 1, b + 1); // web
    indices.push(a + 2, a + 3, b + 2, b + 2, a + 3, b + 3); // flange
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  const mesh = new Mesh('rafter', scene);
  vd.applyToMesh(mesh);
  mesh.material = mat;
  return mesh;
}

/** A thin member (cable, tie) between two points: two crossed quads, visible from any side. */
function member(scene: Scene, p0: Vector3, p1: Vector3, width: number, mat: StandardMaterial): Mesh {
  const d = p1.subtract(p0).normalize();
  const a = Vector3.Cross(d, Vector3.Up()).normalize().scale(width / 2);
  const b = Vector3.Cross(d, a).normalize().scale(width / 2);
  const positions: number[] = [];
  const indices: number[] = [];
  for (const [k, side] of [a, b].entries()) {
    for (const p of [p0.subtract(side), p0.add(side), p1.subtract(side), p1.add(side)]) positions.push(p.x, p.y, p.z);
    const o = k * 4;
    indices.push(o, o + 1, o + 2, o + 2, o + 1, o + 3);
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = [0, 0, 1, 0, 0, 1, 1, 1, 0, 0, 1, 0, 0, 1, 1, 1];
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  const mesh = new Mesh('member', scene);
  vd.applyToMesh(mesh);
  mesh.material = mat;
  return mesh;
}

/** A square column from the ground up to `height`, aligned with the straight. */
function column(scene: Scene, f: StraightFrame, along: number, out: number, height: number, size: number, mat: StandardMaterial): Mesh {
  const c = MeshBuilder.CreateBox('column', { width: size, height, depth: size }, scene);
  c.rotation.y = f.heading;
  c.position = f.point(along, out, height / 2);
  c.material = mat;
  return c;
}

/** Largest barrier offset along a stretch on one side. */
function barrierAlong(track: Track, s0: number, s1: number, side: 1 | -1): number {
  let max = 0;
  const L = track.length;
  const span = (s1 - s0 + L) % L;
  for (let d = 0; d <= span; d += 25) {
    const f = track.frameAt((s0 + d) % L);
    const p = track.project(f.x + f.tz * side * 5, f.z - f.tx * side * 5);
    max = Math.max(max, barrierOffsetAt(track, p));
  }
  return max;
}

interface Mats {
  crowd: StandardMaterial;
  concrete: StandardMaterial;
  canopy: StandardMaterial;
  roof: StandardMaterial;
  boards: StandardMaterial;
  facade: StandardMaterial;
  steel: StandardMaterial;
  standBack: StandardMaterial;
}

/** Grandstand facing the track: fascia, raked crowd deck, back wall, canopies on masts. */
function grandstand(scene: Scene, track: Track, name: string, s0: number, s1: number, side: 1 | -1, mats: Mats): { meshes: Mesh[]; footprint: Footprint } {
  const f = straightFrame(track, s0, s1, side);
  const B = barrierAlong(track, s0, s1, side) + 3;
  const depth = 22;
  const rake = 0.55;
  const front = 3;
  const top = front + depth * rake;
  const meshes: Mesh[] = [
    strip(scene, `${name}-fascia`, f, 0, f.length, [[B, 0], [B, front]], mats.boards),
    strip(scene, `${name}-deck`, f, 0, f.length, [[B + 0.3, front], [B + depth, top]], mats.crowd),
    strip(scene, `${name}-back`, f, 0, f.length, [[B + depth, 0], [B + depth, top + 1.5]], mats.standBack),
  ];
  // Separate canopies with gaps, like Sepang's leaf roofs: each is cantilevered
  // from columns behind the back wall, rises gently towards the track, and is
  // held by stays from a mast. From below: a membrane over purlins (texture),
  // darker towards the recessed back, tapered steel rafters every 6 m and a
  // white fascia along the front edge.
  const bay = 42;
  const span = bay - 6;
  const back = B + depth + 1.5;
  const roof: Array<[number, number]> = [
    [back, top + 3.0],
    [B + depth * 0.72, top + 4.0],
    [B + depth * 0.45, top + 4.6],
    [B + depth * 0.18, top + 4.95],
    [B + 0.8, top + 5.0],
  ];
  const tip = roof[roof.length - 1]!;
  const heightAt = (out: number): number => {
    for (let i = 1; i < roof.length; i++) {
      const [o0, u0] = roof[i - 1]!;
      const [o1, u1] = roof[i]!;
      if (out <= o0 && out >= o1) return u0 + ((u1 - u0) * (o0 - out)) / (o0 - o1);
    }
    return tip[1];
  };
  for (let a = 4; a + span <= f.length; a += bay) {
    meshes.push(strip(scene, `${name}-roof`, f, a, a + span, roof, mats.roof, [0.58, 0.68, 0.79, 0.9, 1]));
    // Fascia: a deep white edge band with a return lip, and a gutter at the back.
    meshes.push(
      strip(scene, `${name}-roof-fascia`, f, a - 0.15, a + span + 0.15, [
        [tip[0] + 0.9, tip[1] - 0.95],
        [tip[0] - 0.1, tip[1] - 0.95],
        [tip[0] - 0.1, tip[1] + 0.25],
      ], mats.canopy),
      strip(scene, `${name}-roof-gutter`, f, a - 0.15, a + span + 0.15, [
        [back + 0.1, top + 3.2],
        [back + 0.1, top + 2.3],
      ], mats.canopy),
    );
    for (let k = 0; k <= span / 6; k++) {
      const along = a + k * 6;
      meshes.push(rafter(scene, f, along, roof, 1.5, 0.35, 0.35, mats.steel));
      // Columns under every other rafter, just behind the back wall.
      if (k % 2 === 1) meshes.push(column(scene, f, along, back - 0.3, top + 3.0 - 1.5, 0.6, mats.steel));
    }
    // Mast behind the middle of the canopy, with stays out to the front third.
    const mid = a + span / 2;
    const mastTop = top + 12;
    meshes.push(column(scene, f, mid, back + 1.2, mastTop, 0.7, mats.steel));
    const head = f.point(mid, back + 1.2, mastTop - 0.3);
    const reach = B + depth * 0.3;
    for (const d of [-span * 0.42, -span * 0.18, span * 0.18, span * 0.42]) {
      meshes.push(member(scene, head, f.point(mid + d, reach, heightAt(reach) + 0.05), 0.09, mats.steel));
    }
    // Back-stay down to the ground anchor.
    meshes.push(member(scene, head, f.point(mid, back + 9, 0), 0.12, mats.steel));
  }
  const mid = f.point(f.length / 2, B + depth / 2, 0);
  // Deep enough behind the stand to keep trees off the masts and back-stays.
  return { meshes, footprint: { x: mid.x, z: mid.z, halfLength: f.length / 2 + 6, halfDepth: depth / 2 + 14, heading: f.heading } };
}

/**
 * Pit building with garages and a glass upper floor: straight, along the
 * main straight, with its front where track/pitLane.ts puts the garages (from
 * the OSM building). The lane surface itself is built by render/pitLane.ts.
 */
function pitComplex(scene: Scene, track: Track, pit: PitLane, mats: Mats): { meshes: Mesh[]; footprint: Footprint } {
  const f = straightFrame(track, pit.garages.fromS, pit.garages.toS, pit.entryOffsetSide);
  const P = pit.garages.trackOffset;
  const depth = 20;
  const height = 12;
  const meshes: Mesh[] = [
    strip(scene, 'pit-facade', f, 0, f.length, [[P, -0.3], [P, height]], mats.facade),
    strip(scene, 'pit-roof', f, 0, f.length, [[P - 3, height + 0.2], [P + depth, height + 0.2]], mats.concrete),
    strip(scene, 'pit-back', f, 0, f.length, [[P + depth, -0.3], [P + depth, height]], mats.concrete),
  ];
  // Gable ends: a frame whose "along" is height, at each end of the building.
  for (const end of [0, f.length]) {
    const g: StraightFrame = { ...f, point: (along, out, up) => f.point(end, out, along + up) };
    meshes.push(strip(scene, 'pit-end', g, -0.3, height + 0.2, [[P, 0], [P + depth, 0]], mats.concrete));
  }
  const mid = f.point(f.length / 2, P + depth / 2, 0);
  return { meshes, footprint: { x: mid.x, z: mid.z, halfLength: f.length / 2 + 12, halfDepth: depth / 2 + 6, heading: f.heading } };
}

/** Vegetation keepout over the pit roads and lane (plus a margin), for scatterVegetation. */
export function pitKeepout(pit: PitLane, margin = 8): (x: number, z: number) => boolean {
  return (x, z) => pit.inFootprint(x, z, margin);
}

function insideFootprint(x: number, z: number, f: Footprint): boolean {
  const dx = x - f.x;
  const dz = z - f.z;
  // Rotate into the box frame: along = heading direction (sin h, cos h).
  const along = dx * Math.sin(f.heading) + dz * Math.cos(f.heading);
  const across = dx * Math.cos(f.heading) - dz * Math.sin(f.heading);
  return Math.abs(along) < f.halfLength && Math.abs(across) < f.halfDepth;
}

/** Treeline silhouette: irregular canopy tops, for the distant forest ring. */
function treelineTexture(scene: Scene): DynamicTexture {
  const w = 1024;
  const h = 128;
  const tex = new DynamicTexture('tex-treeline', { width: w, height: h }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const rand = rng(61);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#2b4a2a';
  ctx.fillRect(0, h * 0.55, w, h * 0.45);
  for (let x = 0; x < w; x += 6) {
    const r = 10 + rand() * 26;
    const y = h * 0.62 - rand() * h * 0.38;
    ctx.fillStyle = rand() < 0.5 ? '#2f5230' : '#264526';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Occasional palm crowns poking above the canopy.
  ctx.strokeStyle = '#2f5230';
  ctx.lineWidth = 3;
  for (let i = 0; i < 14; i++) {
    const x = rand() * w;
    const crown = h * 0.15 + rand() * h * 0.15;
    ctx.beginPath();
    ctx.moveTo(x, h * 0.5);
    ctx.lineTo(x, crown);
    ctx.stroke();
    for (let k = 0; k < 7; k++) {
      const a = Math.PI * (k / 6);
      ctx.beginPath();
      ctx.moveTo(x, crown);
      ctx.quadraticCurveTo(x - Math.cos(a) * 14, crown - 8, x - Math.cos(a) * 22, crown + 8);
      ctx.stroke();
    }
  }
  tex.hasAlpha = true;
  tex.wrapU = WRAP;
  tex.update(true);
  return tex;
}

/** A ring of forest on the horizon: an alpha-tested treeline fading into the haze. */
function horizonForest(scene: Scene, track: Track, floor: number): Mesh {
  let cx = 0;
  let cz = 0;
  for (const p of track.points) {
    cx += p.x;
    cz += p.z;
  }
  cx /= track.count;
  cz /= track.count;
  let reach = 0;
  for (const p of track.points) reach = Math.max(reach, Math.hypot(p.x - cx, p.z - cz));
  const n = 96;
  const rand = rng(9);
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  let first: [number, number] | undefined;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = i === n ? first![0] : reach + 620 + (rand() - 0.5) * 120;
    const h = i === n ? first![1] : 30 + (rand() - 0.5) * 16;
    if (i === 0) first = [r, h];
    const x = cx + Math.cos(a) * r;
    const z = cz + Math.sin(a) * r;
    // v = 0 at the bottom, 1 at the canopy tops (the canvas top is v = 1).
    // Base below the lowest ground so the treeline never floats.
    positions.push(x, floor - 3, z, x, h, z);
    uvs.push((i / n) * 32, 0, (i / n) * 32, 1);
    if (i < n) indices.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 2, i * 2 + 1, i * 2 + 3);
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  const ring = new Mesh('horizon-forest', scene);
  vd.applyToMesh(ring);
  const mat = new StandardMaterial('horizon-forest-mat', scene);
  mat.diffuseTexture = treelineTexture(scene);
  mat.useAlphaFromDiffuseTexture = true;
  mat.transparencyMode = 1;
  mat.alphaCutOff = 0.4;
  mat.backFaceCulling = false;
  mat.specularColor = Color3.Black();
  mat.emissiveColor = new Color3(0.08, 0.1, 0.07);
  ring.material = mat;
  ring.isPickable = false;
  ring.freezeWorldMatrix();
  return ring;
}

/** Sepang scenery. Distances are along our centreline (the main straight crosses the line at s = 0). */
export function buildSepangScenery(scene: Scene, track: Track, groundAt: (x: number, z: number) => number, floor: number): Mesh[] {
  const L = track.length;
  const mats: Mats = {
    crowd: material(scene, 'stand-crowd-mat', Color3.White(), crowdTexture(scene), 0.22),
    concrete: material(scene, 'concrete-mat', new Color3(0.78, 0.78, 0.76)),
    canopy: material(scene, 'canopy-mat', new Color3(0.95, 0.95, 0.97), undefined, 0.35),
    roof: material(scene, 'roof-under-mat', Color3.White(), roofUndersideTexture(scene)),
    boards: material(scene, 'fascia-mat', Color3.White(), undefined, 0.1),
    facade: material(scene, 'facade-mat', Color3.White(), facadeTexture(scene), 0.12),
    steel: material(scene, 'steel-mat', new Color3(0.42, 0.45, 0.5), undefined, 0.06),
    standBack: material(scene, 'stand-back-mat', Color3.White(), standBackTexture(scene), 0.1),
  };
  // Roof undersides: warm bounce light off the track and the crowd.
  mats.roof.emissiveColor = new Color3(0.36, 0.33, 0.29);
  // Fascia boards reuse the barrier boards: u is metres along, v spans the 3 m fascia.
  // DynamicTexture.clone() copies neither the canvas nor the tiling, and a
  // texture that was never updated is never ready (the fascia didn't draw, and
  // the scene never became ready for the track materials to be frozen).
  const boardsTex = barrierTexture(scene);
  const fasciaTex = boardsTex.clone();
  fasciaTex.getContext().drawImage(boardsTex.getContext().canvas, 0, 0);
  fasciaTex.update(true);
  fasciaTex.uScale = boardsTex.uScale;
  fasciaTex.anisotropicFilteringLevel = boardsTex.anisotropicFilteringLevel;
  fasciaTex.vScale = 1 / 3;
  mats.boards.diffuseTexture = fasciaTex;

  // Main grandstand between the straights: one side faces the main straight
  // (on its left), the other faces the back straight (also on its left).
  const main = grandstand(scene, track, 'stand-main', L - 190, 250, -1, mats);
  const back = grandstand(scene, track, 'stand-back', 4520, 4960, -1, mats);
  const pit = sepangPitLane(track);
  const pits = pitComplex(scene, track, pit, mats);
  const built = [...main.meshes, ...back.meshes, ...pits.meshes];
  const footprints = [main.footprint, back.footprint, pits.footprint];
  const onPit = pitKeepout(pit);

  // Merge static pieces per material to keep draw calls down.
  const merged: Mesh[] = [];
  for (const mat of new Set(built.map((m) => m.material))) {
    const group = built.filter((m) => m.material === mat);
    const one = group.length > 1 ? Mesh.MergeMeshes(group, true, true) : group[0];
    if (one) {
      one.isPickable = false;
      one.freezeWorldMatrix();
      merged.push(one);
    }
  }

  merged.push(...buildVegetation(scene, track, (x, z) => footprints.some((fp) => insideFootprint(x, z, fp)) || onPit(x, z), sceneryParams.trees.value, groundAt));
  merged.push(horizonForest(scene, track, floor));
  return merged;
}
